import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useOptionalDependency } from 'ditox-react';
import { useSubscription } from 'observable-hooks';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { EMPTY } from 'rxjs';
import { COMM_MANAGER_TOKEN, I18N_TOKEN, INTERCOM_MANAGER_TOKEN, SERVICE_WORKER_MANAGER_TOKEN } from '@/container';
import useSignOut from '@/hooks/users/use-sign-out';
import { type CommEvent, getNavigateRoute } from '@/models/events/comm-event';
import type { IntercomEvent } from '@/models/events/intercom-event';
import type { CommMessage } from '@/models/messages/comm-message';
import ROUTES from '@/routes';
import { getUserId, useAuthStore } from '@/stores/auth';
import { promisifySessionsHydration, useConversationSessionStore } from '@/stores/conversation-session';
import { useMessageStore } from '@/stores/message';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { addToast } from '@/stores/toast';
import { generateSessionKey } from '@/utils/session';
import { collectionListQueryOptions } from '../collections/use-collections';
import { friendsQueryOptions } from '../contacts/use-friends';
import { groupsQueryOptions } from '../contacts/use-groups';
import { allChatSessionQueryOptions } from '../sessions/use-conversation-sessions';
import { notificationSettingsQueryOptions } from '../settings/use-notification-settings';
import { tagListQueryOptions } from '../tags/use-tags';
import { generateFriendInfoQueryOptions } from '../users/use-friend-info';

export default function useComm() {
  const commManager = useOptionalDependency(COMM_MANAGER_TOKEN);
  const intercomManager = useOptionalDependency(INTERCOM_MANAGER_TOKEN);
  const serviceWorkerManager = useOptionalDependency(SERVICE_WORKER_MANAGER_TOKEN);
  const i18n = useOptionalDependency(I18N_TOKEN);
  const navigate = useNavigate();
  const { mutate: signOut } = useSignOut();
  const queryClient = useQueryClient();
  const { subscribeSessionByUserId, unSubscribeSessionByUserId } = useConversationSessionStore();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { t } = useTranslation();
  const { auth } = useAuthStore();

  useSubscription(
    commManager?.event$ ?? EMPTY,
    async (event) => {
      switch (event.event) {
        case 'sign-out':
          signOut({});
          break;
        case 'change-locale':
          i18n?.changeLanguage(event.data.locale.toLowerCase());
          dayjs.locale(event.data.locale.toLowerCase());
          break;
        case 'close':
          navigate(ROUTES.MESSAGES.$path(), { state: { from: 'comm' } });
          break;
        case 'navigate-back':
          if (location.state?.from === 'comm' || searchParams.get('from') === 'onboard') {
            commManager?.dispatch({ event: 'dismiss' });
          } else {
            navigate(-1);
          }
          break;
        case 'change-notification-settings':
          await queryClient.invalidateQueries(notificationSettingsQueryOptions);
          break;
        case 'check-version-update': {
          const response = await serviceWorkerManager?.checkVersionUpdate();
          response &&
            commManager?.dispatch({
              event: 'version-update',
              data: {
                version: response?.key,
                hasUpdate: response?.hasUpdate,
              },
            });
          break;
        }
        case 'trigger-version-update':
          await serviceWorkerManager?.triggerServiceWorkerUpdate();
          commManager?.dispatch({ event: 'reload', data: { cause: 'sw-update-comm' } });
          break;
        case 'session':
          switch (event.data.type) {
            case 'group':
              navigate(ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id: event.data.id } }), {
                state: { from: 'comm' },
              });
              break;
            case 'session':
              navigate(ROUTES.MESSAGES.SESSIONS.$buildPath({ params: { id: event.data.id } }), {
                state: { from: 'comm' },
              });
              break;
          }
          break;
        case 'request-user-session':
          navigate(
            ROUTES.USER_BY_ID.$buildPath({
              params: { id: event.data.id },
              searchParams: { from: 'comm' },
            }),
          );
          break;
        case 'group-info':
          navigate(
            ROUTES.GROUP_BY_ID.$buildPath({
              params: { id: event.data.id },
              searchParams: { from: 'comm', remark: event.data.remark },
            }),
          );
          break;
        case 'subscribe-session':
          switch (event.data.type) {
            case 'new-message':
              if (event.data.session?.type === 'user') {
                subscribeSessionByUserId(event.data.session.id);
              }
          }
          break;
        case 'unsubscribe-session':
          switch (event.data.type) {
            case 'new-message':
              if (event.data.session?.type === 'user') {
                unSubscribeSessionByUserId(event.data.session.id);
              }
          }
          break;
        case 'join-group':
          await queryClient.fetchQuery(allChatSessionQueryOptions);
          queryClient.invalidateQueries(groupsQueryOptions);
          intercomManager?.dispatch({ event: 'join-group' });
          break;
        case 'open':
          if (event.data?.navigate) {
            navigate(getNavigateRoute(event.data.navigate));
          }
          break;
      }
    },
    undefined,
    () => {
      commManager?.dispatch({ event: 'terminated' });
    },
  );

  useSubscription(intercomManager?.event$ ?? EMPTY, async (event) => {
    switch (event.event) {
      case 'clear-all-device-session':
        try {
          if (auth?.UserID !== event.data.userId) return;
          await promisifySessionsHydration();
          const sessions = await queryClient.ensureQueryData(allChatSessionQueryOptions);
          const groups = await queryClient.ensureQueryData(groupsQueryOptions);
          for (const session of sessions) {
            usePeriodicMessageStore
              .getState()
              .setPurgedMessageId(generateSessionKey(session.SessionType, session.ID), session.LastMsgID);
            await useMessageStore.getState().hideAll(session.SessionType, session.ID);
            if (session.SessionType === 'group') {
              const group = groups[session.ID];
              if (group?.Info) {
                useConversationSessionStore
                  .getState()
                  .setLastReadNotice(generateSessionKey('group', session.ID), group.Info);
              }
            }
          }
          useConversationSessionStore.getState().hideAllCurrentSessions();
          commManager?.dispatch({ event: 'new-message', data: { count: 0 } });
          intercomManager?.dispatch({ event: 'clear-all-device-session-success', data: { userId: auth.UserID } });
        } catch (e) {
          if (auth?.UserID) {
            intercomManager?.dispatch({ event: 'clear-all-device-session-failed', data: { userId: auth.UserID } });
          }
        }
        break;
      case 'clear-all-device-session-success':
        if (auth?.UserID === event.data.userId) {
          addToast({
            type: 'success',
            message: t('messaging-settings.success'),
          });
        }
        break;
      case 'clear-all-device-session-failed':
        if (auth?.UserID === event.data.userId) {
          addToast({
            type: 'error',
            message: t('messaging-settings.failed'),
          });
        }
        break;
      case 'update-auth-credentials':
        await useAuthStore.persist.rehydrate();
        break;
      case 'auth-credential-in-use':
        {
          if (!event.data) {
            const userId = getUserId();
            if (userId) {
              intercomManager?.dispatch({
                event: 'auth-credential-in-use',
                data: { userId },
              });
            }
          }
        }
        break;
      case 'join-group':
        await queryClient.fetchQuery(allChatSessionQueryOptions);
        queryClient.invalidateQueries(groupsQueryOptions);
        break;
      case 'update-collection':
        queryClient.invalidateQueries(collectionListQueryOptions);
        break;
      case 'leave-session':
        if (event.data.type === 'user') {
          await Promise.allSettled([
            queryClient.invalidateQueries(friendsQueryOptions),
            queryClient.invalidateQueries(allChatSessionQueryOptions),
            queryClient.invalidateQueries(generateFriendInfoQueryOptions(event.data.id)),
            queryClient.invalidateQueries(tagListQueryOptions),
            queryClient.invalidateQueries({ queryKey: ['/GetTagMembers'] }),
          ]);
        }
    }
  });

  return {
    commManager,
    intercomManager,
  };
}

export function useCommDispatcher() {
  const manager = useOptionalDependency(COMM_MANAGER_TOKEN);

  const handleDispatch = useCallback(
    (message: CommMessage) => {
      manager?.dispatch(message);
    },
    [manager],
  );

  const handleDispatchAsync = useCallback(
    <K extends CommEvent['event']>(message: CommMessage, awaitEventNames: K[], timeoutMs = 15_000) => {
      if (!manager) return Promise.reject('CommManager is not available');
      return manager.dispatchAsync(message, awaitEventNames, timeoutMs);
    },
    [manager],
  );

  return {
    dispatch: handleDispatch,
    dispatchAsync: handleDispatchAsync,
  };
}

export function useIntercomDispatcher() {
  const manager = useOptionalDependency(INTERCOM_MANAGER_TOKEN);

  const handleDispatch = useCallback(
    (message: IntercomEvent) => {
      manager?.dispatch(message);
    },
    [manager],
  );

  const handleDispatchAsync = useCallback(
    <K extends IntercomEvent['event']>(message: IntercomEvent, awaitEventNames: K[], timeoutMs = 60_000) => {
      if (!manager) return Promise.reject('IntercomManager is not available');
      return manager.dispatchAsync(message, awaitEventNames, timeoutMs);
    },
    [manager],
  );

  return {
    dispatch: handleDispatch,
    dispatchAsync: handleDispatchAsync,
  };
}
